import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../../config/environment';

// Re-export for use in other API slices
export { API_BASE_URL };

/**
 * Log API errors to console with detailed information
 * @param {string} endpoint - The API endpoint
 * @param {string} method - HTTP method
 * @param {object} error - Error object from RTK Query
 * @param {object} args - Request arguments
 */
const logApiError = (endpoint, method, error, args) => {
  const timestamp = new Date().toISOString();
  const errorDetails = {
    timestamp,
    endpoint,
    method,
    status: error?.status,
    statusText: error?.data?.error || error?.error || 'Unknown error',
    code: error?.data?.code,
    details: error?.data?.details,
    originalArgs: args,
  };
  
  console.group(`❌ API Error: ${method} ${endpoint}`);
  console.error('Timestamp:', timestamp);
  console.error('Status:', error?.status);
  console.error('Message:', error?.data?.error || error?.error || 'Unknown error');
  if (error?.data?.code) {
    console.error('Error Code:', error?.data?.code);
  }
  if (error?.data?.details) {
    console.error('Details:', error?.data?.details);
  }
  if (error?.data?.validation_errors) {
    console.error('Validation Errors:', error?.data?.validation_errors);
  }
  console.error('Request Args:', args);
  console.groupEnd();
  
  // Store error in sessionStorage for debugging (last 10 errors)
  try {
    const storedErrors = JSON.parse(sessionStorage.getItem('apiErrors') || '[]');
    storedErrors.unshift(errorDetails);
    sessionStorage.setItem('apiErrors', JSON.stringify(storedErrors.slice(0, 10)));
  } catch (e) {
    // Ignore storage errors
  }
  
  return errorDetails;
};

/**
 * Log successful API calls (only in development)
 */
const logApiSuccess = (endpoint, method, data) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`✅ API Success: ${method} ${endpoint}`, data);
  }
};

// Custom baseQuery with authentication and token refresh
const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { endpoint, arg }) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Send both headers to satisfy all backend checks
      headers.set('X-Auth-Token', token);
      headers.set('Authorization', `Bearer ${token}`);
    }

    const requestBody = typeof arg === 'object' && arg !== null ? arg.body : undefined;

    // Handle FormData - remove Content-Type so browser sets it with boundary
    if (requestBody instanceof FormData) {
      headers.delete('Content-Type');
    } else if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  },
});

// Wrapper for automatic token renewal on 401 and enhanced error handling
const baseQueryWithReauth = async (args, api, extraOptions) => {
  // Extract endpoint info for logging
  const endpoint = typeof args === 'string' ? args : args.url;
  const method = typeof args === 'string' ? 'GET' : (args.method || 'GET');
  
  let result = await baseQuery(args, api, extraOptions);

  // Log the request in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔄 API Request: ${method} ${endpoint}`, typeof args === 'object' ? args.body : undefined);
  }

  // Handle errors
  if (result?.error) {
    // Handle 401 - Token expired
    if (result.error.status === 401) {
      console.log('🔄 401 detected, attempting token renewal...');
      
      // Try to refresh the token
      const refreshResult = await baseQuery(
        { url: '/auth/renew', method: 'POST' },
        api,
        extraOptions
      );

      if (refreshResult?.data) {
        const { token } = refreshResult.data;
        
        // Store the new token
        localStorage.setItem('token', token);
        
        // Also update axiosInstance defaults to keep them in sync
        // (for legacy code that still uses axiosInstance)
        try {
          const { default: axiosInstance } = await import('../../services/axiosInstance');
          axiosInstance.defaults.headers.common['X-Auth-Token'] = token;
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } catch (e) {
          // Ignore if axiosInstance is not available
        }
        
        console.log('✅ Token renewed successfully');
        
        // Do not retry if body is FormData: it is single-use and already consumed by the first request.
        // Returning the 401 lets the mutation fail so the user can retry the action (e.g. re-upload).
        const body = typeof args === 'object' && args != null ? args.body : undefined;
        if (body instanceof FormData) {
          // Token is refreshed; next attempt will use new token
          return result;
        }
        
        // Retry the original request
        result = await baseQuery(args, api, extraOptions);
      } else {
        // Token refresh failed - clear auth and redirect to login
        console.error('❌ Token renewal failed, redirecting to login...');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    } else {
      // Log non-401 errors
      logApiError(endpoint, method, result.error, args);
    }
  } else if (result?.data) {
    // Log successful response
    logApiSuccess(endpoint, method, result.data);
  }

  return result;
};

// Base API - all other API slices will inject endpoints into this
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  // Tag types for cache invalidation
  tagTypes: [
    'Auth',
    'User',
    'Role',
    'Permission',
    'Person',
    'Vehicle',
    'Product',
    'ProductImage',
    'ProductDocument',
    'ProductMetadata',
    'ProductTranslation',
    'Piece',
    'PieceImage',
    'PieceMaterial',
    'Material',
    'MaterialImage',
    'Manufacturer',
    'ManufacturerMetadata',
    'Proposal',
    'ProposalItem',
    'ProposalItemImage',
    'ProposalItemPieceMaterial',
    'Order',
    'Route',
    'Language',
    'Reference',
    'Dashboard',
    'CarelliItem',
    'ExchangeRate',
    'PdfSnapshot',
    'Deal',
    'ZendeskContact',
    'ZendeskSync',
    'Note',
    'InternalTask',
  ],
  endpoints: () => ({}),
});

/**
 * Helper function to extract error message from RTK Query error
 * Use this in components to get a user-friendly error message
 * @param {object} error - Error object from RTK Query mutation/query
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred';
  
  // RTK Query error format
  if (error.data?.error) return error.data.error;
  if (error.data?.message) return error.data.message;
  if (error.error) return error.error;
  
  // HTTP status-based messages
  const statusMessages = {
    400: 'Invalid request. Please check your input.',
    401: 'You are not authorized. Please log in again.',
    403: 'Access denied. You don\'t have permission for this action.',
    404: 'The requested resource was not found.',
    409: 'This action conflicts with existing data.',
    422: 'Validation error. Please check your input.',
    500: 'Server error. Please try again later.',
    502: 'Service temporarily unavailable.',
    503: 'Service temporarily unavailable.',
  };
  
  if (error.status && statusMessages[error.status]) {
    return statusMessages[error.status];
  }
  
  // Network error
  if (error.status === 'FETCH_ERROR') {
    return 'Unable to connect to the server. Please check your internet connection.';
  }
  
  // Parsing error
  if (error.status === 'PARSING_ERROR') {
    return 'Received an invalid response from the server.';
  }
  
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Get stored API errors for debugging
 * @returns {Array} Array of recent API errors
 */
export const getStoredApiErrors = () => {
  try {
    return JSON.parse(sessionStorage.getItem('apiErrors') || '[]');
  } catch (e) {
    return [];
  }
};

/**
 * Clear stored API errors
 */
export const clearStoredApiErrors = () => {
  sessionStorage.removeItem('apiErrors');
};
